import { type RefObject, useEffect, useRef } from 'react'

/**
 * The open surfaces that cover the page, outermost first.
 *
 * Escape belongs to the last one alone. Without the stack a confirm opened over
 * a sheet takes both away with one press: each listener hears the same key and
 * each closes the thing it owns, so answering a question would also discard the
 * record behind it.
 */
const covering: symbol[] = []

/**
 * What a surface that COVERS the page owes: Escape, a way back to where focus
 * came from, and the page behind it taken out of reach.
 *
 * One hook, because a page can be covered by a sheet, a modal, or a modal over
 * a sheet, and three separate answers to "am I on top" disagree the first time
 * two are open at once.
 *
 * Not for a Companion. That one accompanies work that continues underneath, so
 * the page stays live and reachable by the keyboard — inerting it is the whole
 * difference between the two archetypes, and it is what this hook does.
 *
 * `panel` needs `tabIndex={-1}`: focus moves onto it at open, since the control
 * that was focused is about to become unreachable.
 */
export function useDismissable(
  panel: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  // Read at keypress rather than closed over, so a call site passing an inline
  // arrow does not re-run the effect — and re-entering the stack on every
  // render would put this surface back on top of one opened above it.
  const latest = useRef(onClose)
  useEffect(() => {
    latest.current = onClose
  })

  useEffect(() => {
    const el = panel.current
    if (!el) return

    const me = Symbol('covering')
    covering.push(me)

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (covering[covering.length - 1] !== me) return
      // A list open inside this surface closes on the same key and marks it
      // handled; the key was spent closing the list, not the surface.
      if (e.defaultPrevented) return
      // Marked handled: the surface is gone before the key reaches a band
      // listening further out, so the band can only tell by this mark.
      e.preventDefault()
      latest.current()
    }
    document.addEventListener('keydown', onKey)

    const returnTo = document.activeElement
    // Everything under `body` except the branch this surface portals into. The
    // browser drops an inert subtree from the tab order, so this is the focus
    // trap as well as the guard — there is no second mechanism to keep in step.
    //
    // A live region is spared: it speaks ABOVE the page rather than being part
    // of it, and an inert one is dropped from the accessibility tree — which
    // would silence "Saved." at exactly the moment a surface saves. A branch
    // holding one is descended into rather than spared whole, so a page's own
    // status line keeps the rest of the page out of reach.
    const own = el.closest('body > *')
    const silenced: HTMLElement[] = []
    const silence = (nodes: Iterable<Element>) => {
      for (const node of nodes) {
        if (node === own || !(node instanceof HTMLElement) || node.inert)
          continue
        if (node.matches('[aria-live]')) continue
        if (node.querySelector('[aria-live]')) silence(node.children)
        else silenced.push(node)
      }
    }
    silence(document.body.children)
    for (const node of silenced) node.inert = true

    el.focus()

    return () => {
      document.removeEventListener('keydown', onKey)
      covering.splice(covering.indexOf(me), 1)
      for (const node of silenced) node.inert = false
      if (returnTo instanceof HTMLElement) returnTo.focus()
    }
  }, [panel])
}
